import React from "react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, INR, fmtDate, titleCase } from "@/lib/format";
import { FormDialog } from "@/components/EntityForm";
import { DataTable } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Card, run } from "@/components/agent/AgentPanels";
import { Plus, Download, AlertTriangle } from "lucide-react";

const PAY_REF = [{ name: "payment_reference", label: "Payment / UTR reference", required: true, full: true }];

export function PayoutStatusActions({ p, reload, path = "payouts" }) {
  const { hasPerm } = useAuth();
  if (!hasPerm("agents:payout") || p.status === "paid" || p.status === "rejected") return null;
  const set = (status) => run(() => api.put(`/agents/${path}/${p.id}`, { status }), `Marked ${titleCase(status)}`).then(reload);
  return (
    <div className="flex gap-1 justify-end" onClick={(e) => e.stopPropagation()}>
      {p.status !== "approved" && <Button size="sm" variant="outline" className="h-7" data-testid={`${path}-approve-btn`} onClick={() => set("approved")}>Approve</Button>}
      <FormDialog title="Mark as Paid" fields={PAY_REF} submitLabel="Mark Paid"
        onSubmit={async (v) => { await api.put(`/agents/${path}/${p.id}`, { status: "paid", ...v }); reload(); }}
        trigger={<Button size="sm" className="h-7" data-testid={`${path}-paid-btn`}>Paid</Button>} />
      {path === "payouts"
        ? p.status !== "on_hold" && <Button size="sm" variant="ghost" className="h-7" data-testid="payouts-hold-btn" onClick={() => set("on_hold")}>Hold</Button>
        : <Button size="sm" variant="ghost" className="h-7" data-testid="withdrawals-reject-btn" onClick={() => set("rejected")}>Reject</Button>}
    </div>
  );
}

export const payoutColumns = (reload, withAgent) => [
  { key: "payout_code", header: "Payout", render: (p) => <div><div className="font-mono text-xs">{p.payout_code}</div>{withAgent && <div className="text-xs font-semibold">{p.agent_name}</div>}</div> },
  { key: "source", header: "Line", render: (p) => <span className="text-sm">{titleCase(p.source)}<div className="text-[11px] text-muted-foreground">{p.reference || ""} {p.period || ""}</div></span> },
  { key: "gross_commission", header: "Gross", render: (p) => INR(p.gross_commission) },
  { key: "payout_amount", header: "Payout", render: (p) => <span>{INR(p.payout_amount)}<div className="text-[11px] text-muted-foreground">{p.payout_percent}%</div></span> },
  { key: "tds_amount", header: "TDS", render: (p) => <span>{INR(p.tds_amount)}<div className="text-[11px] text-muted-foreground">{p.tds_percent}%</div></span> },
  { key: "gst_amount", header: "GST", render: (p) => p.gst_applicable ? INR(p.gst_amount) : <span className="text-xs text-muted-foreground">N/A</span> },
  { key: "net_payable", header: "Net", render: (p) => <span className="font-semibold">{INR(p.net_payable)}</span> },
  { key: "status", header: "Status", render: (p) => <div><StatusBadge value={p.status} />{p.payment_reference && <div className="text-[11px] text-muted-foreground mt-0.5">{p.payment_reference}</div>}</div> },
  { key: "a", header: "", render: (p) => <PayoutStatusActions p={p} reload={reload} /> },
];

export function PayoutsPanel({ agent, payouts, profile, reload }) {
  const { hasPerm } = useAuth();
  const fields = [
    { name: "source", label: "Business Line", type: "select", required: true, options: [{ value: "insurance", label: "Insurance" }, { value: "loan", label: "Loan" }] },
    { name: "gross_commission", label: "Gross commission received (₹)", type: "number", required: true },
    { name: "payout_percent", label: "Agent payout %", type: "number" },
    { name: "tds_percent", label: "TDS % (blank = 5% with PAN / 20% without)", type: "number" },
    { name: "reference", label: "Policy / Loan reference" },
    { name: "period", label: "Period (e.g. Jun-2026)" },
    { name: "notes", label: "Notes", type: "textarea", full: true },
  ];
  const create = async (v) => {
    const body = { ...v };
    if (body.tds_percent === "" || body.tds_percent == null) delete body.tds_percent;
    await api.post(`/agents/${agent.id}/payouts`, body); reload();
  };
  return (
    <Card title="Payout Ledger" actions={<>
      {hasPerm("agents:payout") && <Button size="sm" variant="outline" data-testid="agent-statement-btn" onClick={() => window.open(`${API}/agents/${agent.id}/statement`, "_blank")}><Download className="h-4 w-4 mr-1" />Statement CSV</Button>}
      {hasPerm("agents:payout") && <FormDialog title="Add Payout Entry" fields={fields} initial={{ source: (agent.business_lines || ["insurance"])[0], payout_percent: agent.payout_percent_default }}
        onSubmit={create} submitLabel="Add Payout" testId="create-payout-dialog"
        trigger={<Button size="sm" data-testid="create-payout-btn"><Plus className="h-4 w-4 mr-1" />Add Payout</Button>} />}
    </>}>
      {profile && !profile.payout_eligible && (
        <div className="mb-3 flex items-center gap-2 text-sm rounded-lg bg-rose-50 dark:bg-rose-950/40 text-rose-800 dark:text-rose-300 px-3 py-2" data-testid="payout-block-banner">
          <AlertTriangle className="h-4 w-4" /> Payouts cannot be approved or paid: {profile.payout_block_reason}
        </div>
      )}
      <DataTable columns={payoutColumns(reload)} rows={payouts} rowTestId="agent-payout-row" emptyText="No payout entries yet" />
    </Card>
  );
}

export function WithdrawalsPanel({ agent, wd, reload }) {
  const { hasPerm } = useAuth();
  const fields = [
    { name: "amount", label: "Amount (₹)", type: "number", required: true },
    { name: "mode", label: "Mode", type: "select", options: [{ value: "bank_transfer", label: "Bank Transfer" }, { value: "upi", label: "UPI" }, { value: "cheque", label: "Cheque" }] },
    { name: "notes", label: "Notes", type: "textarea", full: true },
  ];
  const cols = [
    { key: "withdrawal_code", header: "Request", render: (w) => <span className="font-mono text-xs">{w.withdrawal_code}</span> },
    { key: "amount", header: "Amount", render: (w) => <span className="font-semibold">{INR(w.amount)}</span> },
    { key: "mode", header: "Mode", render: (w) => titleCase(w.mode) },
    { key: "created_at", header: "Requested", render: (w) => fmtDate(w.created_at) },
    { key: "status", header: "Status", render: (w) => <div><StatusBadge value={w.status} />{w.payment_reference && <div className="text-[11px] text-muted-foreground">{w.payment_reference}</div>}</div> },
    { key: "a", header: "", render: (w) => <PayoutStatusActions p={w} reload={reload} path="withdrawals" /> },
  ];
  return (
    <Card title="Withdrawals" actions={hasPerm("agents:payout") && (
      <FormDialog title="New Withdrawal" fields={fields} initial={{ mode: "bank_transfer" }} submitLabel="Create Request"
        onSubmit={async (v) => { await api.post(`/agents/${agent.id}/withdrawals`, v); reload(); }}
        trigger={<Button size="sm" data-testid="create-withdrawal-btn"><Plus className="h-4 w-4 mr-1" />New Withdrawal</Button>} />
    )}>
      <div className="grid grid-cols-3 gap-3 mb-3 text-sm">
        <div><div className="text-muted-foreground text-xs">Earned (approved + paid)</div><div className="font-bold" data-testid="wd-earned">{INR(wd.total_earned)}</div></div>
        <div><div className="text-muted-foreground text-xs">Withdrawn / requested</div><div className="font-bold" data-testid="wd-taken">{INR(wd.total_withdrawn)}</div></div>
        <div><div className="text-muted-foreground text-xs">Available</div><div className="font-bold text-emerald-600" data-testid="wd-available">{INR(wd.available_balance)}</div></div>
      </div>
      <DataTable columns={cols} rows={wd.items || []} rowTestId="agent-withdrawal-row" emptyText="No withdrawals yet" />
    </Card>
  );
}
